import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  Send, MessageCircle, Bell, Loader2, Users, X, ExternalLink, Trash2, Upload, Image as ImageIcon, Film, Mic,
  ChevronDown, ChevronUp, History, BookmarkPlus, ChevronsUpDown, MousePointerClick, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { id: "all", label: "Tous" },
  { id: "passenger", label: "Passagers" },
  { id: "driver", label: "Chauffeurs" },
  { id: "partner", label: "Partenaires" },
];

function AudienceFilters({ role, setRole, city, setCity, count, cities,
  approvedOnly, setApprovedOnly, minBookings, setMinBookings, minTft, setMinTft }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="mb-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wide text-[#64748B] mb-1 block">Rôle</label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="h-10" data-testid="broadcast-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ROLES.map(r => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wide text-[#64748B] mb-1 block">Ville</label>
          <Select value={city || "_all"} onValueChange={(v) => setCity(v === "_all" ? "" : v)}>
            <SelectTrigger className="h-10" data-testid="broadcast-city"><SelectValue placeholder="Toutes les villes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Toutes les villes</SelectItem>
              {cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wide text-[#64748B] mb-1 block">Destinataires</label>
          <div className="h-10 rounded-md border border-[#E2E8F0] bg-[#F1F5F9] flex items-center px-3 gap-2 text-sm font-bold text-[#0A2540]" data-testid="broadcast-audience-count">
            <Users className="w-4 h-4 text-[#64748B]" />
            <span>{count}</span>
            <span className="text-xs font-normal text-[#64748B]">utilisateur{count > 1 ? "s" : ""}</span>
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-[#0A2540] hover:text-[#FACC15] transition"
        data-testid="toggle-advanced-filters"
      >
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />} Filtres avancés
      </button>
      {expanded && (
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-3 bg-[#F1F5F9] rounded-xl p-3" data-testid="advanced-filters-panel">
          <label className="flex items-center gap-2 text-xs text-[#0A2540] cursor-pointer" data-testid="filter-approved-only-wrap">
            <input
              type="checkbox"
              checked={approvedOnly}
              onChange={(e) => setApprovedOnly(e.target.checked)}
              disabled={role !== "driver"}
              className="w-4 h-4 accent-[#0A2540]"
              data-testid="filter-approved-only"
            />
            <span className={role !== "driver" ? "text-[#94A3B8]" : ""}>Chauffeurs approuvés uniquement</span>
          </label>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wide text-[#64748B] mb-0.5 block">Min. réservations (passagers)</label>
            <Input
              type="number"
              min={0}
              value={minBookings}
              onChange={(e) => setMinBookings(parseInt(e.target.value || "0", 10) || 0)}
              disabled={role === "driver" || role === "partner"}
              className="h-9 text-xs"
              data-testid="filter-min-bookings"
              placeholder="0"
            />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wide text-[#64748B] mb-0.5 block">Min. balance TFT (partenaires)</label>
            <Input
              type="number"
              min={0}
              value={minTft}
              onChange={(e) => setMinTft(parseInt(e.target.value || "0", 10) || 0)}
              disabled={role !== "partner"}
              className="h-9 text-xs"
              data-testid="filter-min-tft"
              placeholder="0"
            />
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------ Template picker ------------ */
function TemplatePicker({ kind, onLoad, currentContent }) {
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchTemplates = async () => {
    try {
      const { data } = await api.get(`/admin/broadcast/templates?kind=${kind}`);
      setTemplates(data || []);
    } catch (_e) { /* silent */ }
  };
  useEffect(() => { fetchTemplates(); }, [kind]); // eslint-disable-line

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await api.post("/admin/broadcast/templates", { kind, name: name.trim(), ...currentContent });
      toast.success("Modèle enregistré");
      setName("");
      setSaveOpen(false);
      await fetchTemplates();
    } catch (err) {
      toast.error("Erreur", { description: err.response?.data?.detail || "Échec" });
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce modèle ?")) return;
    try {
      await api.delete(`/admin/broadcast/templates/${id}`);
      toast.success("Modèle supprimé");
      await fetchTemplates();
    } catch (_e) { toast.error("Erreur suppression"); }
  };

  return (
    <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid={`template-picker-${kind}`}>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="inline-flex items-center gap-1 h-9 px-3 rounded-full border border-[#E2E8F0] text-xs font-semibold text-[#0A2540] hover:border-[#0A2540] bg-white"
          data-testid={`load-template-btn-${kind}`}
        >
          <ChevronsUpDown className="w-3 h-3" /> Modèles ({templates.length})
        </button>
        {open && (
          <div className="absolute top-11 left-0 z-10 w-72 max-h-80 overflow-y-auto bg-white border border-[#E2E8F0] rounded-xl shadow-lg" data-testid={`templates-list-${kind}`}>
            {templates.length === 0 ? (
              <p className="p-4 text-xs text-[#64748B] text-center">Aucun modèle enregistré</p>
            ) : (
              templates.map(t => (
                <div key={t.id} className="flex items-center gap-2 px-3 py-2 hover:bg-[#F1F5F9] border-b border-[#F1F5F9]" data-testid={`template-${t.id}`}>
                  <button
                    type="button"
                    onClick={() => { onLoad(t); setOpen(false); }}
                    className="flex-1 text-left"
                  >
                    <p className="text-xs font-bold text-[#0A2540]">{t.name}</p>
                    <p className="text-[10px] text-[#64748B] truncate">{(t.title || t.body).slice(0, 60)}</p>
                  </button>
                  <button type="button" onClick={() => handleDelete(t.id)} className="w-6 h-6 rounded-full bg-[#EF4444]/10 text-[#EF4444] flex items-center justify-center hover:bg-[#EF4444] hover:text-white">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </div>
      {saveOpen ? (
        <div className="flex items-center gap-1">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nom du modèle"
            className="h-9 text-xs w-40"
            autoFocus
            data-testid={`template-name-input-${kind}`}
          />
          <Button onClick={handleSave} disabled={saving || !name.trim()} size="sm" className="h-9 bg-[#FACC15] text-[#0A2540] hover:bg-[#EAB308]" data-testid={`template-save-confirm-${kind}`}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Sauver"}
          </Button>
          <Button onClick={() => setSaveOpen(false)} size="sm" variant="outline" className="h-9"><X className="w-3.5 h-3.5" /></Button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setSaveOpen(true)}
          className="inline-flex items-center gap-1 h-9 px-3 rounded-full border border-dashed border-[#CBD5E1] text-xs font-semibold text-[#0A2540] hover:border-[#0A2540]"
          data-testid={`save-template-btn-${kind}`}
        >
          <BookmarkPlus className="w-3 h-3" /> Enregistrer comme modèle
        </button>
      )}
    </div>
  );
}

/* ------------ WhatsApp tab ------------ */
function WhatsAppTab({ filters }) {
  const { role, city, count } = filters;
  const [message, setMessage] = useState("");
  const [media, setMedia] = useState([]); // [{url, type, name}]
  const [uploading, setUploading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [sending, setSending] = useState(false);

  const canGenerate = message.trim().length > 0 && count > 0;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 30 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 30 Mo)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/broadcast/whatsapp/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMedia((m) => [...m, { url: data.url, type: data.content_type, name: data.filename }]);
      toast.success("Média ajouté");
    } catch (err) {
      toast.error("Échec de l'upload", { description: err.response?.data?.detail || "Erreur" });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleGenerate = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/broadcast/whatsapp", {
        role, city: city || null,
        approved_only: filters.approvedOnly,
        min_bookings: filters.minBookings,
        min_tft: filters.minTft,
        message,
        media_urls: media.map(m => m.url),
      });
      setContacts(data.contacts || []);
      toast.success(`${data.count} contacts générés`, { description: "Cliquez sur chacun pour envoyer." });
    } catch (err) {
      toast.error("Erreur", { description: err.response?.data?.detail || "Génération impossible" });
    } finally {
      setSending(false);
    }
  };

  const handleLoadTemplate = (t) => {
    setMessage(t.body || "");
    toast.success(`Modèle "${t.name}" chargé`);
  };

  return (
    <div className="space-y-4">
      <AudienceFilters {...filters} />
      <TemplatePicker kind="whatsapp" onLoad={handleLoadTemplate} currentContent={{ body: message }} />
      <div>
        <label className="text-[11px] font-bold uppercase tracking-wide text-[#64748B] mb-1 block">Message</label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, 4000))}
          rows={6}
          placeholder="Bonjour, message pour la communauté TrigoTour…"
          className="w-full rounded-xl border border-[#E2E8F0] p-3 text-sm text-[#0A2540] focus:border-[#0A2540] focus:ring-2 focus:ring-[#0A2540]/10 outline-none resize-none"
          data-testid="whatsapp-message"
        />
        <p className="text-[10px] text-right text-[#94A3B8]">{message.length}/4000</p>
      </div>
      <div>
        <label className="text-[11px] font-bold uppercase tracking-wide text-[#64748B] mb-1 block">Médias (optionnel)</label>
        <div className="flex flex-wrap gap-2">
          {media.map((m, i) => {
            const isImg = m.type?.startsWith("image/");
            const isVid = m.type?.startsWith("video/");
            const Icon = isImg ? ImageIcon : isVid ? Film : Mic;
            return (
              <div key={i} className="inline-flex items-center gap-2 rounded-full bg-[#F1F5F9] pl-2 pr-1 py-1" data-testid={`whatsapp-media-${i}`}>
                <Icon className="w-3.5 h-3.5 text-[#0A2540]" />
                <span className="text-xs text-[#0A2540] max-w-[120px] truncate">{m.name}</span>
                <button type="button" onClick={() => setMedia(media.filter((_, j) => j !== i))} className="w-5 h-5 rounded-full bg-[#EF4444]/10 text-[#EF4444] flex items-center justify-center">
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
          <label className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-[#CBD5E1] px-3 py-1.5 cursor-pointer hover:border-[#0A2540] text-xs font-semibold text-[#0A2540]" data-testid="whatsapp-upload-btn">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {uploading ? "Envoi…" : "Ajouter (image / vidéo / audio)"}
            <input type="file" accept="image/*,video/*,audio/*" className="hidden" onChange={handleUpload} disabled={uploading} />
          </label>
        </div>
        <p className="text-[10px] text-[#94A3B8] mt-1">Les liens des médias seront insérés dans le message WhatsApp (aperçu automatique).</p>
      </div>

      <Button
        onClick={handleGenerate}
        disabled={!canGenerate || sending}
        className="w-full h-12 rounded-full bg-[#25D366] text-white hover:bg-[#1FB855] font-bold text-sm"
        data-testid="generate-whatsapp-contacts"
      >
        {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <MessageCircle className="w-4 h-4 mr-1" />}
        Générer la liste des contacts WhatsApp ({count})
      </Button>

      {contacts.length > 0 && (
        <div className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden" data-testid="whatsapp-contacts-list">
          <div className="px-4 py-2 bg-[#F1F5F9] flex items-center justify-between">
            <p className="text-xs font-bold text-[#0A2540]">
              {contacts.length} contacts prêts à recevoir le message
            </p>
            <button type="button" onClick={() => setContacts([])} className="text-[11px] text-[#64748B] hover:text-[#EF4444] inline-flex items-center gap-1">
              <Trash2 className="w-3 h-3" /> Vider
            </button>
          </div>
          <div className="max-h-80 overflow-y-auto divide-y divide-[#F1F5F9]">
            {contacts.map((c) => (
              <div key={c.user_id} className="flex items-center gap-3 px-4 py-2.5">
                <div className="w-8 h-8 rounded-full bg-[#0A2540] text-[#FACC15] font-bold flex items-center justify-center text-xs shrink-0">
                  {(c.name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-[#0A2540] truncate">{c.name}</p>
                  <p className="text-[11px] text-[#64748B] font-mono">{c.phone} · {c.role}</p>
                </div>
                <a
                  href={c.wa_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-[#25D366] hover:bg-[#1FB855] text-white text-xs font-bold shrink-0"
                  data-testid={`send-whatsapp-${c.user_id}`}
                >
                  <Send className="w-3 h-3" /> Envoyer
                </a>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------ Flash tab ------------ */
function FlashTab({ filters }) {
  const { role, city, count } = filters;
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [ctaLabel, setCtaLabel] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");
  const [sending, setSending] = useState(false);

  const canSend = title.trim().length > 0 && count > 0;

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Image uniquement"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/broadcast/whatsapp/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImageUrl(data.url);
      toast.success("Image ajoutée");
    } catch (err) {
      toast.error("Échec de l'upload", { description: err.response?.data?.detail || "Erreur" });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/broadcast/flash", {
        role, city: city || null,
        approved_only: filters.approvedOnly,
        min_bookings: filters.minBookings,
        min_tft: filters.minTft,
        title, body,
        image_url: imageUrl || null,
        cta_label: ctaLabel || null,
        cta_url: ctaUrl || null,
      });
      toast.success(`Flash envoyé à ${data.sent} utilisateurs (sur ${data.matched} ciblés)`);
      setTitle(""); setBody(""); setImageUrl(""); setCtaLabel(""); setCtaUrl("");
    } catch (err) {
      toast.error("Erreur", { description: err.response?.data?.detail || "Envoi impossible" });
    } finally {
      setSending(false);
    }
  };

  const handleLoadTemplate = (t) => {
    setTitle(t.title || "");
    setBody(t.body || "");
    setCtaLabel(t.cta_label || "");
    setCtaUrl(t.cta_url || "");
    setImageUrl(t.image_url || "");
    toast.success(`Modèle "${t.name}" chargé`);
  };

  return (
    <div className="space-y-4">
      <AudienceFilters {...filters} />
      <TemplatePicker
        kind="flash"
        onLoad={handleLoadTemplate}
        currentContent={{ title, body, cta_label: ctaLabel, cta_url: ctaUrl, image_url: imageUrl }}
      />
      <div>
        <label className="text-[11px] font-bold uppercase tracking-wide text-[#64748B] mb-1 block">Titre*</label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value.slice(0, 140))}
          placeholder="Ex : Nouveau service disponible !"
          className="h-11"
          data-testid="flash-title"
        />
      </div>
      <div>
        <label className="text-[11px] font-bold uppercase tracking-wide text-[#64748B] mb-1 block">Message</label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value.slice(0, 2000))}
          rows={4}
          placeholder="Détails du message (optionnel)…"
          className="w-full rounded-xl border border-[#E2E8F0] p-3 text-sm text-[#0A2540] focus:border-[#0A2540] focus:ring-2 focus:ring-[#0A2540]/10 outline-none resize-none"
          data-testid="flash-body"
        />
        <p className="text-[10px] text-right text-[#94A3B8]">{body.length}/2000</p>
      </div>
      <div>
        <label className="text-[11px] font-bold uppercase tracking-wide text-[#64748B] mb-1 block">Image (optionnel)</label>
        {imageUrl ? (
          <div className="rounded-xl overflow-hidden border border-[#E2E8F0] relative">
            <img src={imageUrl} alt="" className="w-full max-h-40 object-cover" />
            <button type="button" onClick={() => setImageUrl("")} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/95 flex items-center justify-center hover:bg-[#EF4444] hover:text-white transition">
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <label className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-[#CBD5E1] px-3 py-1.5 cursor-pointer hover:border-[#0A2540] text-xs font-semibold text-[#0A2540]" data-testid="flash-upload-image">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
            {uploading ? "Envoi…" : "Ajouter une image"}
            <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploading} />
          </label>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wide text-[#64748B] mb-1 block">Bouton d'action (optionnel)</label>
          <Input value={ctaLabel} onChange={(e) => setCtaLabel(e.target.value.slice(0, 40))} placeholder="Voir l'offre" className="h-10" data-testid="flash-cta-label" />
        </div>
        <div>
          <label className="text-[11px] font-bold uppercase tracking-wide text-[#64748B] mb-1 block">Lien du bouton</label>
          <Input value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)} placeholder="/vehicles ou https://…" className="h-10" data-testid="flash-cta-url" />
        </div>
      </div>
      <Button
        onClick={handleSend}
        disabled={!canSend || sending}
        className="w-full h-12 rounded-full bg-[#FACC15] hover:bg-[#EAB308] text-[#0A2540] font-extrabold text-sm shadow-lg shadow-[#FACC15]/25"
        data-testid="send-flash-btn"
      >
        {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Bell className="w-4 h-4 mr-1" />}
        Envoyer le flash à {count} utilisateur{count > 1 ? "s" : ""}
      </Button>
      <p className="text-[10px] text-[#94A3B8] text-center">
        ⚡ Le flash apparaît en modal centré chez tous les utilisateurs connectés. Ceux qui sont hors-ligne ne le recevront pas.
      </p>
    </div>
  );
}

/* ------------ History tab ------------ */
function HistoryTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/broadcast/history?limit=50");
      setRows(data || []);
    } catch (_e) { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchHistory(); }, []);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch (_e) { return iso; }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-[#64748B]">
          <History className="w-3.5 h-3.5 inline mr-1" /> Historique des 50 derniers envois
        </p>
        <button type="button" onClick={fetchHistory} disabled={loading} className="text-[11px] text-[#0A2540] hover:underline inline-flex items-center gap-1">
          {loading && <Loader2 className="w-3 h-3 animate-spin" />} Actualiser
        </button>
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#0A2540]" /></div>
      ) : rows.length === 0 ? (
        <p className="text-center py-8 text-sm text-[#64748B]">Aucun broadcast pour le moment.</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-[#E2E8F0]">
          <table className="min-w-full text-xs" data-testid="broadcast-history-table">
            <thead>
              <tr className="bg-[#F1F5F9] text-[#64748B] uppercase text-[10px]">
                <th className="text-left px-3 py-2 font-bold">Date</th>
                <th className="text-left px-3 py-2 font-bold">Type</th>
                <th className="text-left px-3 py-2 font-bold">Admin</th>
                <th className="text-left px-3 py-2 font-bold">Cible</th>
                <th className="text-right px-3 py-2 font-bold">Envoyés</th>
                <th className="text-right px-3 py-2 font-bold">Clics</th>
                <th className="text-left px-3 py-2 font-bold">Aperçu</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#F1F5F9]">
              {rows.map(r => {
                const isFlash = r.kind === "flash";
                const target = [r.role === "all" ? "Tous" : r.role, r.city, r.approved_only ? "approuvés" : "", r.min_bookings ? `≥${r.min_bookings} rés.` : "", r.min_tft ? `≥${r.min_tft} TFT` : ""].filter(Boolean).join(" · ");
                const ctr = r.recipients_count > 0 && r.click_count > 0
                  ? Math.round((r.click_count / r.recipients_count) * 1000) / 10 : null;
                return (
                  <tr key={r.id} data-testid={`broadcast-row-${r.id}`}>
                    <td className="px-3 py-2 text-[#0A2540] whitespace-nowrap">{fmtDate(r.created_at)}</td>
                    <td className="px-3 py-2">
                      {isFlash ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FACC15]/20 text-[#0A2540] font-bold text-[10px]">
                          <Bell className="w-3 h-3" /> Flash
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#25D366]/20 text-[#065F46] font-bold text-[10px]">
                          <MessageCircle className="w-3 h-3" /> WhatsApp
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[#0A2540]">{r.admin_name || r.admin_email || "—"}</td>
                    <td className="px-3 py-2 text-[#64748B]">{target || "—"}</td>
                    <td className="px-3 py-2 text-right font-bold text-[#0A2540]">{r.recipients_count || 0}</td>
                    <td className="px-3 py-2 text-right">
                      {isFlash ? (
                        <span className="inline-flex items-center gap-1 font-mono text-[#0A2540]" data-testid={`click-count-${r.id}`}>
                          <MousePointerClick className="w-3 h-3" /> {r.click_count || 0}
                          {ctr !== null && <span className="text-[9px] text-[#10B981] flex items-center gap-0.5 ml-1"><TrendingUp className="w-2.5 h-2.5" />{ctr}%</span>}
                        </span>
                      ) : (
                        <span className="text-[#94A3B8]">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 max-w-xs">
                      <p className="truncate text-[#0A2540]" title={isFlash ? `${r.title || ""} — ${r.body || ""}` : r.message || ""}>
                        {isFlash ? (r.title || r.body || "—") : (r.message || "—").slice(0, 80)}
                      </p>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ------------ Main panel ------------ */
export default function AdminBroadcastPanel() {
  const [activeTab, setActiveTab] = useState("whatsapp");
  const [role, setRole] = useState("all");
  const [city, setCity] = useState("");
  const [approvedOnly, setApprovedOnly] = useState(false);
  const [minBookings, setMinBookings] = useState(0);
  const [minTft, setMinTft] = useState(0);
  const [count, setCount] = useState(0);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    let cancel = false;
    const q = new URLSearchParams({ role, ...(city && { city }), approved_only: String(approvedOnly), min_bookings: String(minBookings), min_tft: String(minTft) });
    api.get(`/admin/broadcast/audience?${q.toString()}`)
      .then(({ data }) => {
        if (cancel) return;
        setCount(data.total || 0);
        setCities(data.available_cities || []);
      })
      .catch(() => { if (!cancel) setCount(0); });
    return () => { cancel = true; };
  }, [role, city, approvedOnly, minBookings, minTft]);

  const filters = { role, setRole, city, setCity, count, cities, approvedOnly, setApprovedOnly, minBookings, setMinBookings, minTft, setMinTft };

  return (
    <div className="bg-white rounded-xl border border-[#CBD5E1] p-5 sm:p-6" style={{ fontFamily: "Outfit, Inter, sans-serif" }} data-testid="admin-broadcast-panel">
      <div className="flex items-center gap-2 mb-2">
        <span className="w-10 h-10 rounded-full bg-[#FACC15]/20 flex items-center justify-center">
          <Bell className="w-5 h-5 text-[#0A2540]" />
        </span>
        <div>
          <h2 className="text-lg font-extrabold text-[#0A2540]" style={{ fontFamily: "Outfit" }}>Communication</h2>
          <p className="text-xs text-[#64748B]">Diffuser un message aux utilisateurs de TrigoTour.</p>
        </div>
      </div>

      <div className="flex items-center gap-2 my-4 border-b border-[#E2E8F0]">
        <button
          type="button"
          onClick={() => setActiveTab("whatsapp")}
          className={`px-4 py-2 text-sm font-bold rounded-t-lg -mb-px transition ${activeTab === "whatsapp" ? "text-[#25D366] border-b-2 border-[#25D366]" : "text-[#64748B] hover:text-[#0A2540]"}`}
          data-testid="broadcast-tab-whatsapp"
        >
          <MessageCircle className="w-3.5 h-3.5 inline mr-1" /> WhatsApp
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("flash")}
          className={`px-4 py-2 text-sm font-bold rounded-t-lg -mb-px transition ${activeTab === "flash" ? "text-[#FACC15] border-b-2 border-[#FACC15]" : "text-[#64748B] hover:text-[#0A2540]"}`}
          data-testid="broadcast-tab-flash"
        >
          <Bell className="w-3.5 h-3.5 inline mr-1" /> Flash WebSocket
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("history")}
          className={`px-4 py-2 text-sm font-bold rounded-t-lg -mb-px transition ${activeTab === "history" ? "text-[#0A2540] border-b-2 border-[#0A2540]" : "text-[#64748B] hover:text-[#0A2540]"}`}
          data-testid="broadcast-tab-history"
        >
          <History className="w-3.5 h-3.5 inline mr-1" /> Historique
        </button>
      </div>

      {activeTab === "whatsapp" && <WhatsAppTab filters={filters} />}
      {activeTab === "flash" && <FlashTab filters={filters} />}
      {activeTab === "history" && <HistoryTab />}
    </div>
  );
}
